import type { ISbStoriesParams } from 'storyblok-js-client'
import { isClient } from '@vueuse/core'
import { useStructuredData } from 'core/services/ldjson'

export function useStoryblokBlog() {
  const nuxt = useNuxtApp()
  const route = useRoute()
  const version = route.query._storyblok ? 'draft' : nuxt.$config.public.storyblokVersion
  return {
    getHome: async (params: ISbStoriesParams = {}) => {
      const res = await useFetch('/api/storyblok/v2/blog/home', { query: { params, version } })
      if (res.error.value)
        throw res.error.value
      return res.data.value
    },
    getArticles: async (page: number, filter: any = {}, params: ISbStoriesParams = {}) => {
      const res = await useFetch('/api/storyblok/v2/blog/articles', { query: { page, filter, params, version } })
      if (res.error.value)
        throw res.error.value
      return res.data.value as any[]
    },
    getArticle: async (path: string) => {
      const res = await useFetch('/api/storyblok/v2/blog/article', { query: { path, version } })
      if (res.error.value)
        throw res.error.value
      if (res.data.value?.redirect) {
        if (res.data.value.redirect.status?.toString().startsWith('3'))
          nuxt.runWithContext(() => navigateTo(useTrailingSlash(res.data.value.redirect.location), { redirectCode: res.data.value.redirect.status, external: true }))
        showError({ status: res.data.value.redirect.status })
      }
      return res.data.value
    },
    getAllArticles: async (params: ISbStoriesParams) => {
      const res = await useFetch('/api/storyblok/v2/blog/all-articles', { query: { params, version } })
      if (res.error.value)
        throw res.error.value
      return res.data.value as { links: { name: string; path: string }[]; total: number }
    },
  }
}

export function useDocumentTemplate(document: StoryBlokRichText, values: Record<string, any>) {
  return JSON.parse(useTemplate(JSON.stringify(document))(values)) as StoryBlokRichText
}

export function useStoryblokMeta(data: StoryblokPage) {
  const route = useRoute()
  const config = useRuntimeConfig()
  const breadcrumbs = useState('breadcrumbs')
  breadcrumbs.value = { breadcrumbs: data?.breadcrumbs, title: data?.title }

  function resolveTitle() {
    if (data.removeSiteName) return data.title
    return `${data.title} | ${config.public.siteName}`
  }

  // HEAD TAGS
  const meta = data.metaTags?.map(({ name, content }) => ({ name, content })) || []
  data.options?.includes('disableIndex')
    ? meta.push({ name: 'robots', content: 'noindex, nofollow' })
    : meta.push({ name: 'robots', content: 'index, follow' })
  const canonical = new URL(data.canonicalTag?.cachedUrl || data.canonicalTag?.url || route.path, config.public.host)
  const ogImage = data.openGraphImage?.filename.replace('a.storyblok.com', 'img2.storyblok.com/1200x627/filters:format(jpg)')
  const ogImageDefault = new URL('/og-default.jpg', config.public.host)
  const openGraph = [
    { property: 'og:title', content: data.title },
    { property: 'og:type', content: 'website' },
    { property: 'og:image:', content: ogImage || ogImageDefault.href },
    { property: 'og:url', content: canonical.origin + canonical.pathname },
    { property: 'og:site_name', content: config.public.siteName as any },
  ]
  useHead({
    title: resolveTitle(),
    meta: [
      { name: 'description', content: data.metaDescription },
      ...openGraph,
      ...meta,
    ],
    link: [{
      rel: 'canonical',
      href: canonical.origin + canonical.pathname,
    }],
  })

  if (data.name) {
    const { structuredData } = useStructuredData(data.name,
      data.image,
      data.startDate,
      data.endDate,
    )
    useHead({
      script: [{ children: structuredData, type: 'application/ld+json' }],
    })
  }
  if (isClient) {
    const ga = useAnalytics({
      p_product_family: data.p_product_family || 'uncategorized',
      p_product: data.p_product || 'uncategorized',
      cg1: data.cg1 || '',
      cg2: data.cg2 || 'uncategorized',
      cg3: data.cg3 || '',
      cg4: data.cg4 || 'uncategorized',
      cg5: data.cg5 || '',
      uid: data.uid || '',
      page_name: data.page_name || '',
      page_content_type: data.page_content_type,
      event_type: 'uncategorized',
    })
    ga?.pageEventTrack()
    ga?.addCustomEvent({ content_platform: 'storyblok' })
  }
}

export function useStoryblokBridge() {
  const route = useRoute()
  const cookie = useCookie('storyblok-preview')
  if (route.query._storyblok) {
    useScriptTag(
      'https://app.storyblok.com/f/storyblok-v2-latest.js',
      () => {
        const storyblokInstance = new window.StoryblokBridge()
        storyblokInstance.on(['published', 'change'], () => {
          window.location.reload()
        })
      },
    )
    cookie.value = 'draft'
  }
}
